/// <amd-module name="Widgets/Burndown/BurndownWidgetComponents/BurndownChartOptionFactory" />
import { Iteration } from "Analytics/Boards/BoardsQueries/IterationsQuery";
import { QueryUtilities } from "Analytics/Common/Utilities/QueryUtilities";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import {
    ChartProps,
    ChartTypesConstants,
    ClickEvent,
    DataPoint,
    DataSeries,
    HybridChartOptions,
    TooltipLineItem,
    TooltipLineItemMarkerType
} from "VSS/Features/Charts/Contracts";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssThemeService } from "VSS/Platform/Theming";
import { DateSampleInterval, parseIsoDateStringInLocalTime } from "WidgetCommon/DateUtils/DateUtils";
import { addDays, addMonths, daysBetweenDates, getEndOfMonth, getStartOfMonth } from "WidgetCommon/DateUtils/DateUtils";
import { ChartOptionFactoryBase } from "WidgetCommon/ModernWidgetTypes/ChartOptionFactoryBase";
import * as Resources from "Widgets/Burndown/Resources";
import { BurnDirection } from "./BurnDirection";
import { BurndownConstants } from "./BurndownConstants";
import { WorkItemEffort } from "./BurndownDataContract";
import { BurndownDataFactory } from "./BurndownDataFactory";

export interface BurndownChartClickInfo {
    seriesName: string;
    dataPointName: string;
    sampleDate: string;
}

export interface BurnChartInputs {
    todayInAccountTimezone: string;
    /** The start date to show on the chart */
    startDate: string;
    /** Whether the chart should animate or not */
    suppressAnimations?: boolean;
    /**
     * The iterations to show on the chart.
     */
    iterations?: Iteration[];
    /**
     * The interval of the generated sample dates.
     * Ignored if the iterations property is set.
     */
    sampleDatesInterval?: DateSampleInterval;
    /**
     * The dates of the work item effort aggregations.
     * The array is ordered by the dates from earliest to latest.
     */
    sampleDates: string[];
    /**
     * Keys are dates, values are the aggregated effort.
     */
    datesToPrimarySeriesEffort: { [key: string]: number };
    /**
     * The average burndown.
     */
    averageBurn: number;
    /**
     * The last date in sampleDates between the start date exclusively and end date inclusively in sampleDates
     * that aligns with the configured sampleDatesInterval.
     * Used by the trendlines as the place to start forecasting from. It is possible for the end date
     * chosen by the user to not align with the date interval chosen, which is why this property is needed.
     * Ignored if the iterations property is set.
     */
    lastGeneratedIntervalSampleDate: string;
    /**
     * The work item effort aggregations.
     */
    datesToTotalEffort: { [key: string]: number };
    /**
     * Completed work item effort data points.
     */
    completedEffort: WorkItemEffort[];
    /**
     * Remaining work item effort data points.
     */
    remainingEffort: WorkItemEffort[];
    /**
     * Whether the chart should show the stacked bar series for completed effort in burndown and remaining effort in burnup.
     */
    showOtherEffort: boolean;
    /**
     * Whether the chart should show the effort burndown/burnup trendline.
     */
    showBurnTrendline: boolean;
    /**
     * Whether the chart should show the effort scope change trendline.
     */
    showScopeTrendline: boolean;
    /**
     * Whether the chart should show the effort scope change trendline.
     */
    skipFutureScopeInTrendLine?: boolean;
    /**
     * Whether the chart should show burnup instead of burndown
     */
    burnDirection: BurnDirection;
    /**
     * Whether the remaining effort should be stacked by work item type.
     */
    showStackedWorkItemTypes: boolean;
    /**
     * The handler for clicking the chart.
     */
    onClick: (clickInfo: BurndownChartClickInfo) => void;
    /**
     * The dictionary mapping work item types to colors that should be used when rendering the chart.
     * Only used if showStackedWorkItemTypes is set to true.
     */
    workItemTypeColorDictionary?: { [key: string]: string };
    /**
     * Boolean flag indicting if the parent dashboard is currently embedded
     */
    allowChartClicks: boolean;

    /** Opt-in flag to treat fill content as a continuous Area, rather than a bar chart */
    showFillAsArea?: boolean;
}

class ChartOptionStrategyFactory {
    public static getStrategy(chartData: BurnChartInputs): ChartOptionStrategy {
        return chartData.iterations != null ? new IterationChartOptionStrategy(chartData) : new DateIntervalChartOptionStrategy(chartData);
    }
}

function getLastElement<T>(array: T[]): T {
    return array[array.length - 1];
}

function getSecondLastElement<T>(array: T[]): T {
    return array[array.length - 2];
}

/**
 * Takes a date string and gets the Browser defined locale date string for that date.
 * @param date a string representation of a date in format YYYY-MM-DD
 * Note: This relies on the date constructor accepting the formaty of this string arg string.
 */
function localeFormatDateString(date: string) {
    return parseIsoDateStringInLocalTime(date).toLocaleDateString();
}

export abstract class ChartOptionStrategy {
    constructor(protected chartInputs: BurnChartInputs) {}

    public abstract getLabelValues(): string[];

    public getBurndownColumnSeries(): DataSeries[] {
        const columnSeries = this.getPrimaryEffortColumnSeries(
            this.chartInputs.remainingEffort,
            Resources.BurndownWidget_RemainingEffortSeriesName,
            BurndownConstants.remainingEffortSeriesColor
        );

        if (this.chartInputs.showOtherEffort) {
            columnSeries.push(this.getCompletedEffortSeriesForBurdownSecondary());
        }

        return columnSeries;
    }

    public getBurnupColumnSeries(): DataSeries[] {
        const columnSeries = this.getPrimaryEffortColumnSeries(
            this.chartInputs.completedEffort,
            Resources.BurndownWidget_CompletedEffortSeriesName,
            BurndownConstants.completedEffortSeriesColor
        );

        if (this.chartInputs.showOtherEffort) {
            columnSeries.push(this.getRemainingEffortSeriesForBurdownSecondary());
        }

        return columnSeries;
    }

    protected getSampleDates(): string[] {
        return this.chartInputs.sampleDates;
    }

    private getPrimaryEffortColumnSeries(effort: WorkItemEffort[], labelName: string, color: string): DataSeries[] {
        if (this.chartInputs.showStackedWorkItemTypes) {
            return getWorkItemStackedEffortColumnSeries(
                effort,
                this.getSampleDates(),
                this.chartInputs.todayInAccountTimezone,
                this.chartInputs.workItemTypeColorDictionary
            );
        } else {
            return [
                {
                    name: labelName,
                    data: reduceEffortToSeriesData(effort, this.getSampleDates(), this.chartInputs.todayInAccountTimezone),
                    color: color
                }
            ];
        }
    }

    private getCompletedEffortSeriesForBurdownSecondary(): DataSeries {
        return {
            name: Resources.BurndownWidget_CompletedEffortSeriesName,
            data: reduceEffortToSeriesData(this.chartInputs.completedEffort, this.getSampleDates(), this.chartInputs.todayInAccountTimezone),
            color: BurndownConstants.completedEffortSeriesColor
        };
    }

    private getRemainingEffortSeriesForBurdownSecondary(): DataSeries {
        return {
            name: Resources.BurndownWidget_RemainingEffortSeriesName,
            data: reduceEffortToSeriesData(this.chartInputs.remainingEffort, this.getSampleDates(), this.chartInputs.todayInAccountTimezone),
            color: BurndownConstants.remainingEffortSeriesColor
        };
    }
}

/** Produces a singular dataset for an unpartitioned series  */
export function reduceEffortToSeriesData(effort: WorkItemEffort[], sampleDates: string[], todayInAccountTimezone: string): (null | number)[] {
    // For each date, sum the Aggregated Effort
    const datesToEffortDictionary = BurndownDataFactory.getEffortByDate(effort);

    // Flatten dictionary to array and ensure that there is data for every date we're charting
    const seriesData = [];
    const dates = sampleDates;
    for (const date of dates) {
        const val =
            datesToEffortDictionary[date] != null
                ? datesToEffortDictionary[date]
                : localeIgnoreCaseComparer(date, todayInAccountTimezone) <= 0 // Is the date earlier than or equal to today?
                ? 0 // Fill holes with 0s on days that have already passed
                : null; // Fill holes with nulls on days in the future
        seriesData.push(val);
    }

    return seriesData;
}

/** Produces a set of series partitioned by work item type. */
export function getWorkItemStackedEffortColumnSeries(
    effort: WorkItemEffort[],
    sampleDates: string[],
    todayInAccountTimezone: string,
    witColorDictionary?: { [key: string]: string }
): DataSeries[] {
    const series = [];

    // Split remaining (burndown) or completed (burnup) effort by work item type
    const effortByWorkItemTypeDictionary: { [key: string]: WorkItemEffort[] } = {};
    for (const e of effort) {
        if (effortByWorkItemTypeDictionary[e.WorkItemType] == null) {
            effortByWorkItemTypeDictionary[e.WorkItemType] = [e];
        } else {
            effortByWorkItemTypeDictionary[e.WorkItemType].push(e);
        }
    }

    // Aggregate effort for each work item type into series
    for (const key in effortByWorkItemTypeDictionary) {
        series.push({
            name: key,
            data: reduceEffortToSeriesData(effortByWorkItemTypeDictionary[key], sampleDates, todayInAccountTimezone),
            color: witColorDictionary != null ? witColorDictionary[key] : undefined
        });
    }

    // Arrange alphabetically by work item type
    series.sort((a, b) => localeIgnoreCaseComparer(a.name, b.name));

    return series;
}

export class IterationChartOptionStrategy extends ChartOptionStrategy {
    public getLabelValues(): string[] {
        const axisLabelValues = [localeFormatDateString(this.chartInputs.startDate)];
        this.chartInputs.iterations && axisLabelValues.push(...this.chartInputs.iterations.map(i => i.IterationName || ""));
        return axisLabelValues;
    }
}

export class DateIntervalChartOptionStrategy extends ChartOptionStrategy {
    public getLabelValues(): string[] {
        return this.chartInputs.sampleDates != null ? this.chartInputs.sampleDates.map(d => localeFormatDateString(d)) : [];
    }
}

/**
 * Takes relevant analytics data, and generates pertinent chart options for rendering by common ChartComponent.
 */
export class BurndownChartOptionFactory extends ChartOptionFactoryBase<BurnChartInputs> {
    /**
     * Filters out elements that evaluate true for isNaN then returns the maximum of the remaining values.
     * @param values - The numbers to compare
     */
    private static maxValue(values: number[]): number {
        if (values == null) {
            throw new Error("values must be defined for maxValue()"); // Dev facing input check. No user-meaningful guidance for users if this was invalid.
        }

        return Math.max(...values.filter(val => !isNaN(val)));
    }

    private static customTooltipMapping(points: DataPoint[]): TooltipLineItem[] {
        return points.map<TooltipLineItem>(point => {
            const roundedVal = Math.round(+point.values[0] * 10) / 10;
            const localedNumber = roundedVal.toLocaleString();

            return {
                styleType: {
                    color: point.color,
                    type: TooltipLineItemMarkerType.Square
                },
                text: `${point.seriesName}: ${localedNumber}`
            };
        });
    }

    constructor(private pageContext: IVssPageContext) {
        super();
    }

    /**
     * Generates chart options for Simple version of Velocity Chart
     * Will need to be re-factored to populate relevant series and x-Axis labelling based on data from server
     */
    public createChartOptions(featureChartData: BurnChartInputs): ChartProps {
        if (featureChartData.sampleDates == null) {
            throw new Error("featureChartData.sampleDates must be defined"); // Dev facing guard statement.
        }

        const strategy = ChartOptionStrategyFactory.getStrategy(featureChartData);

        const options: ChartProps = {
            title: "",
            pageContext: this.pageContext,
            chartType: ChartTypesConstants.Hybrid,
            series: [],
            xAxis: { labelValues: strategy.getLabelValues() },
            yAxis: { endOnTick: false, min: 0, softMax: 1 },
            specializedOptions: { chartTypes: [] },
            suppressAnimation: featureChartData.suppressAnimations
        };

        if (featureChartData.allowChartClicks) {
            options.click = (clickEvent: ClickEvent) => this._handleClick(clickEvent, featureChartData);
        }

        const specializedOptions = options.specializedOptions as HybridChartOptions;

        let columnSeries: DataSeries[];
        if (featureChartData.burnDirection === BurnDirection.Up) {
            columnSeries = strategy.getBurnupColumnSeries();
        } else {
            columnSeries = strategy.getBurndownColumnSeries();
        }

        options.series.push(...columnSeries);
        const fillMode = featureChartData.showFillAsArea ? ChartTypesConstants.StackedArea : ChartTypesConstants.StackedColumn;
        options.series.forEach(() => specializedOptions.chartTypes.push(fillMode));

        const today = featureChartData.todayInAccountTimezone;
        const pastDays = featureChartData.sampleDates.filter(date => date.toLowerCase().localeCompare(today.toLowerCase(), navigator.language) <= 0);

        const datesToTotalEffort = featureChartData.datesToTotalEffort;
        let averageScopeChange = 0;

        if (featureChartData.showScopeTrendline || (featureChartData.burnDirection === BurnDirection.Up && featureChartData.showBurnTrendline)) {
            averageScopeChange = BurndownDataFactory.getAverageChange(datesToTotalEffort, pastDays);
        }

        // The largest plotted trendline value
        let maximumTrendlineValue: number | undefined = undefined;

        if (featureChartData.showBurnTrendline) {
            // List of past values of the burn line (note that this is not guaranteed sorted)
            const values = pastDays.map(key => featureChartData.datesToPrimarySeriesEffort[key]);
            // Value of the last bar in the past (note that the pastDays is sorted, so this is guaranteed last)
            const lastHistoricValue = featureChartData.datesToPrimarySeriesEffort[getLastElement(pastDays)];

            // We adjust the burndown rate to make it hit 0 exactly on a sample date
            let steps: number;
            let adjustedBurnValue: number;

            // Burndown logic
            if (featureChartData.burnDirection === BurnDirection.Down && featureChartData.averageBurn > 0) {
                // If we are burning down, predict how many steps it will take to burn
                steps = Math.ceil(lastHistoricValue / featureChartData.averageBurn);
                // And adjust the burn value so that line intersects with zero at that step exctly
                adjustedBurnValue = lastHistoricValue / steps;
            }
            // Burnup logic
            else if (
                featureChartData.burnDirection === BurnDirection.Up &&
                featureChartData.showBurnTrendline &&
                -featureChartData.averageBurn - averageScopeChange > 0
            ) {
                // If we are burning faster than adding scope, predict how many steps it will take to burn
                steps = Math.ceil(
                    (datesToTotalEffort[getLastElement(pastDays)] - lastHistoricValue) / (-featureChartData.averageBurn - averageScopeChange)
                );
                // And adjust the burn value so that they intersect on that step exactly
                adjustedBurnValue = -(datesToTotalEffort[getLastElement(pastDays)] + averageScopeChange * steps - lastHistoricValue) / steps;
            } else {
                // Don't predict if we're burning the wrong way! (i.e. we are never going to reach zero, or lines are not going to intersect) - just draw trend line going up until chart end
                adjustedBurnValue = featureChartData.averageBurn;
                steps = featureChartData.sampleDates.length - pastDays.length;
            }

            // If we have enough data points to calculate a trend
            if (pastDays.length >= 2) {
                const burnPredictionSteps = this.pushWithinChartTrendPrediction(values, adjustedBurnValue, featureChartData.sampleDates.length);

                if (
                    (featureChartData.averageBurn > 0 && featureChartData.burnDirection === BurnDirection.Down) ||
                    // If average burn is faster than scope change, there's going to be an intersection eventually
                    (-featureChartData.averageBurn > averageScopeChange && featureChartData.burnDirection === BurnDirection.Up)
                ) {
                    // Annotation
                    const lastSampleIsOnInterval =
                        featureChartData.iterations != null ||
                        featureChartData.lastGeneratedIntervalSampleDate === getLastElement(featureChartData.sampleDates);

                    const projectionIsOutsideChart =
                        pastDays.length + steps >
                        (lastSampleIsOnInterval ? featureChartData.sampleDates.length : featureChartData.sampleDates.length - 1);
                    let labelTextFormat: string = Resources.BurndownWidget_ProjectCompletionForecastFormat;
                    let labelColor = undefined;
                    let lineColor = undefined;
                    const themeService = this.pageContext.getService<IVssThemeService>("IVssThemeService");
                    if (themeService) {
                        const textPrimaryColor = themeService.getThemeValue("text-primary-color");
                        labelColor = textPrimaryColor;
                        lineColor = textPrimaryColor;
                    }
                    let axisPosition: number;

                    let projectedCompletionDate: Date;

                    // When the projection goes outside the chart (but will eventually finish), we draw the red Project Completion label.
                    if (projectionIsOutsideChart) {
                        // The predicted position is always at the end of the chart, to right align the label saying so
                        axisPosition = values.length - 1;

                        labelTextFormat = Resources.BurndownWidget_ProjectCompletionForecastOutsideChartFormat;
                        labelColor = themeService
                            ? themeService.getThemeValue("status-error-strong")
                            : BurndownConstants.forecastAnnotationOutsideChartLabelColor;
                        lineColor = "rgba(0,0,0,0)"; // Transparent

                        const stepsOutsideChart =
                            pastDays.length +
                            steps -
                            (lastSampleIsOnInterval ? featureChartData.sampleDates.length : featureChartData.sampleDates.length - 1);
                        let periodName = undefined;
                        if (featureChartData.iterations != null) {
                            const lastChartedDateString = getLastElement(featureChartData.sampleDates);
                            const lastPastIteration = featureChartData.iterations[pastDays.length - 2]; // -1 for the start date and -1 for 0 indexing
                            const iterationLength =
                                lastPastIteration.StartDateTimeOffset && lastPastIteration.EndDateTimeOffset
                                    ? daysBetweenDates(
                                          QueryUtilities.parseAnalyticsDateTime(lastPastIteration.StartDateTimeOffset, true),
                                          QueryUtilities.parseAnalyticsDateTime(lastPastIteration.EndDateTimeOffset, true)
                                      )
                                    : 0;

                            const lastSampleDate = new Date(lastChartedDateString);
                            projectedCompletionDate = addDays(lastSampleDate, iterationLength * stepsOutsideChart);
                            periodName = stepsOutsideChart === 1 ? Resources.TimePeriod_IterationSingular : Resources.TimePeriod_IterationPlural;
                        } else {
                            // Do we need to 'prorate' the last part of the burnline prediction?
                            if (!lastSampleIsOnInterval && featureChartData.sampleDates.length >= 2 && burnPredictionSteps > 0) {
                                // Calculate how long is the last period
                                const lastPeriodLength = daysBetweenDates(
                                    new Date(getLastElement(featureChartData.sampleDates)),
                                    new Date(getSecondLastElement(featureChartData.sampleDates))
                                );
                                let lastPeriodRatio = 1;
                                // If burning down by weeks or months, calculate the length of last period compared to others
                                if (featureChartData.sampleDatesInterval === DateSampleInterval.Weeks) {
                                    lastPeriodRatio = lastPeriodLength / 7;
                                } else if (featureChartData.sampleDatesInterval === DateSampleInterval.Months) {
                                    lastPeriodRatio = lastPeriodLength / 30; // Hardcoded at 30
                                }

                                // Undo initial burndown and burn by prorated amount
                                values[values.length - 1] = getSecondLastElement(values) - adjustedBurnValue * lastPeriodRatio;
                            }

                            const lastChartedDate = parseIsoDateStringInLocalTime(featureChartData.lastGeneratedIntervalSampleDate);
                            switch (featureChartData.sampleDatesInterval) {
                                case DateSampleInterval.Days:
                                    projectedCompletionDate = addDays(lastChartedDate, stepsOutsideChart);
                                    periodName = stepsOutsideChart === 1 ? Resources.TimePeriod_DaySingular : Resources.TimePeriod_DayPlural;
                                    break;
                                case DateSampleInterval.Weeks:
                                    projectedCompletionDate = addDays(lastChartedDate, stepsOutsideChart * 7);
                                    periodName = stepsOutsideChart === 1 ? Resources.TimePeriod_WeekSingular : Resources.TimePeriod_WeekPlural;
                                    break;
                                case DateSampleInterval.Months:
                                default:
                                    // get the last day of the Nth month ahead. use start of current month to avoid skipping a month due to the 31st as a date arg.
                                    projectedCompletionDate = getEndOfMonth(addMonths(getStartOfMonth(lastChartedDate), stepsOutsideChart));
                                    periodName = stepsOutsideChart === 1 ? Resources.TimePeriod_MonthSingular : Resources.TimePeriod_MonthPlural;
                                    break;
                            }
                        }

                        // Filling out only the specific part for outside the chart
                        labelTextFormat = format(labelTextFormat, "{0}", stepsOutsideChart, periodName);
                    } else {
                        // The predicted position is all past days plus number of steps we predicted it will take to intersect
                        // note that axis position is zero based, so subtracting 1 from the length of past days
                        axisPosition = pastDays.length + steps - 1;

                        projectedCompletionDate = parseIsoDateStringInLocalTime(featureChartData.sampleDates[axisPosition]);
                    }

                    const projectedCompletionDateFormattedString = projectedCompletionDate.toLocaleDateString();
                    const labelText = format(labelTextFormat, projectedCompletionDateFormattedString);

                    if (options.xAxis) {
                        options.xAxis.plotLines = [
                            {
                                labelText: labelText,
                                labelColor: labelColor,
                                axisPosition: axisPosition,
                                lineColor: lineColor
                            }
                        ];
                    }
                }
            }

            options.series.push({
                name:
                    featureChartData.burnDirection === BurnDirection.Down
                        ? Resources.BurndownWidget_BurndownTrendlineSeriesName
                        : Resources.BurnupWidget_BurnupTrendlineSeriesName,
                data: values,
                color: BurndownConstants.burnTrendLineColor
            });
            specializedOptions.chartTypes.push(ChartTypesConstants.Line);

            maximumTrendlineValue = BurndownChartOptionFactory.maxValue(([] as number[]).concat(values));
        }

        const totalScopeValues = pastDays.map(key => datesToTotalEffort[key] || 0);

        // The largest value that can be plotted is the highest (non-forecasted) total scope value * 1.25
        const maximumAllowedChartValue = 1.25 * BurndownChartOptionFactory.maxValue(totalScopeValues);

        if (featureChartData.showScopeTrendline) {
            if (!featureChartData.skipFutureScopeInTrendLine) {
                this.pushWithinChartTrendPrediction(totalScopeValues, -averageScopeChange, featureChartData.sampleDates.length);
            }

            options.series.push({
                name: Resources.BurndownWidget_TotalScopeTrendlineSeriesName,
                data: totalScopeValues,
                color: BurndownConstants.scopeTrendLineColor
            });
            specializedOptions.chartTypes.push(ChartTypesConstants.Line);

            maximumTrendlineValue = BurndownChartOptionFactory.maxValue(([] as number[]).concat(totalScopeValues));
        }

        if (maximumTrendlineValue != null && maximumTrendlineValue > maximumAllowedChartValue && options.yAxis) {
            options.yAxis.max = maximumAllowedChartValue;
        }

        options.tooltip = {
            customTooltipMapping: BurndownChartOptionFactory.customTooltipMapping
        };

        return options;
    }

    /**
     * Adds trend prediction to the existing trend values, lower-bound by zero.
     * @param values existing values
     * @param burnRate rate at which to trend
     * @param maxValuesLength maximum length that the resulting values can be
     */
    public pushWithinChartTrendPrediction(values: number[], burnRate: number, maxValuesLength: number): number {
        let burnPreditionSteps = 0;
        const startValue = getLastElement(values);

        while (true) {
            const burnedValue = startValue - (burnPreditionSteps + 1) * burnRate;
            if (
                burnedValue >= 0 && // Stop drawing when we hit zero (if going down).
                values.length < maxValuesLength // Stop drawing when reaching right side of chart
            ) {
                burnPreditionSteps++;
                values.push(burnedValue);
            } else {
                // If we reached zero (for burndown), or ran number of steps predicted by burnup logic above, stop adding more data
                break;
            }
        }
        return burnPreditionSteps;
    }

    /***
     * Interprets the chart click event details into semantically relevant format.
     * Note: this is made public for UT and is also consumed by the click handle in ax sprint burndown widget
     */
    public _handleClick(clickEvent: ClickEvent, chartInputs: BurnChartInputs): void {
        let seriesName = clickEvent.seriesName; // This is the localized name for the clicked series
        const seriesDataIndex = clickEvent.seriesDataIndex; // This corresponds to the position of the iteration in the list.

        // Clicking on the total scope trendline should never do anything.
        if (seriesName === Resources.BurndownWidget_TotalScopeTrendlineSeriesName) {
            return;
        }

        // Clicking on the Burndown/Burnup trendline shouldnt do anything if the date clicked is in the future.
        if (seriesName === Resources.BurndownWidget_BurndownTrendlineSeriesName || seriesName === Resources.BurnupWidget_BurnupTrendlineSeriesName) {
            const now = new Date();
            // Check if the click event is on a future date.
            if (seriesDataIndex && new Date(chartInputs.sampleDates[seriesDataIndex]) > new Date(now.getFullYear(), now.getMonth(), now.getDate())) {
                return;
            } else {
                // Change the series name to get data based on the bar under it.
                seriesName =
                    seriesName === Resources.BurndownWidget_BurndownTrendlineSeriesName
                        ? Resources.BurndownWidget_RemainingEffortSeriesName
                        : Resources.BurndownWidget_CompletedEffortSeriesName;
            }
        }

        chartInputs.onClick({
            seriesName: seriesName || "",
            sampleDate: seriesDataIndex !== undefined ? chartInputs.sampleDates[seriesDataIndex] : "",
            dataPointName: clickEvent.labelName || ""
        });
    }
}
